import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  assessments: [],
  selectedAssessment: null,
  filters: {
    subject: '',
    status: 'all', // 'all', 'pending', 'completed'
    search: '',
    sort: 'dueDate', // 'dueDate', 'createdAt', 'title'
  },
  calendarData: {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    dates: {},
  },
  availableSubjects: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

const studentAssessmentSlice = createSlice({
  name: 'studentAssessment',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Set assessments list
    setAssessments: (state, action) => {
      const { assessments, pagination } = action.payload;
      state.assessments = assessments;
      state.pagination = pagination;
      state.loading = false;
      state.error = null;
    },

    // Append assessments (for infinite scroll)
    appendAssessments: (state, action) => {
      const { assessments, pagination } = action.payload;
      state.assessments = [...state.assessments, ...assessments];
      state.pagination = pagination;
      state.loading = false;
    },

    // Set selected assessment
    setSelectedAssessment: (state, action) => {
      state.selectedAssessment = action.payload;
      state.loading = false;
    },

    // Clear selected assessment
    clearSelectedAssessment: (state) => {
      state.selectedAssessment = null;
    },

    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Reset filters
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Set calendar data
    setCalendarData: (state, action) => {
      state.calendarData = action.payload;
      state.loading = false;
    },

    // Set available subjects
    setAvailableSubjects: (state, action) => {
      state.availableSubjects = action.payload;
    },

    // Update assessment status (after submission)
    updateAssessmentStatus: (state, action) => {
      const { assessmentId, status, submission } = action.payload;
      const assessment = state.assessments.find(a => a._id === assessmentId);
      if (assessment) {
        assessment.status = status;
        assessment.submission = submission;
      }
      if (state.selectedAssessment?._id === assessmentId) {
        state.selectedAssessment.status = status;
        state.selectedAssessment.submission = submission;
        state.selectedAssessment.canAttempt = false;
      }
    },

    // Reset assessments
    resetAssessments: () => initialState,

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

// Export actions
export const {
  setLoading,
  setError,
  setAssessments,
  appendAssessments,
  setSelectedAssessment,
  clearSelectedAssessment,
  setFilters,
  resetFilters,
  setCalendarData,
  setAvailableSubjects,
  updateAssessmentStatus,
  resetAssessments,
  clearError,
} = studentAssessmentSlice.actions;

// Selectors
export const selectAssessments = (state) => state.studentAssessment.assessments;
export const selectSelectedAssessment = (state) => state.studentAssessment.selectedAssessment;
export const selectAssessmentFilters = (state) => state.studentAssessment.filters;
export const selectCalendarData = (state) => state.studentAssessment.calendarData;
export const selectAvailableSubjects = (state) => state.studentAssessment.availableSubjects;
export const selectAssessmentLoading = (state) => state.studentAssessment.loading;
export const selectAssessmentError = (state) => state.studentAssessment.error;
export const selectAssessmentPagination = (state) => state.studentAssessment.pagination;

// Derived selectors
export const selectPendingAssessments = (state) =>
  state.studentAssessment.assessments.filter(a => a.status === 'pending');

export const selectCompletedAssessments = (state) =>
  state.studentAssessment.assessments.filter(a => a.status === 'completed');

export const selectOverdueAssessments = (state) =>
  state.studentAssessment.assessments.filter(a => a.status === 'overdue');

export default studentAssessmentSlice.reducer;
